import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import { useStores } from "@stores";
import { useSeasonsVM } from "@stores/SeasonStore";
import { useLang } from "@src/hooks/useLang";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import Spinner from "@components/Spinner";
import {
  getFieldsWithTrials,
  getTrialsYieldSummary,
  TFieldListItem,
  TTrialWithYield,
} from "@services/fieldsService";

// ── Styled ──────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #899093;
`;

const StyledSelect = styled.select`
  height: 40px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  padding: 0 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  background: #ffffff;
  cursor: pointer;
  min-width: 200px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #68ad18;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.2);
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px;
  }
`;

const Title = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 18px;
  color: #494c5b;
  margin-bottom: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-weight: 700;
  color: #899093;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #f0f2f3;
`;

const Td = styled.td<{ bold?: boolean }>`
  padding: 10px 12px;
  color: #494c5b;
  font-weight: ${({ bold }) => (bold ? 700 : 400)};
  border-bottom: 1px solid #f0f2f3;
`;

const LoadingLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: Nunito Sans, sans-serif;
  font-size: 15px;
  color: #899093;
`;

// ── Color palette for trials ──────────────────────────────
const TRIAL_COLORS = [
  "#68ad18",
  "#2196F3",
  "#FF9800",
  "#9C27B0",
  "#F44336",
  "#00BCD4",
  "#795548",
  "#607D8B",
  "#E91E63",
  "#4CAF50",
];

// ── Component ──────────────────────────────────────────────

const FieldTrials: React.FC = () => {
  const lang = useLang();
  const { settingsStore } = useStores();
  const seasonsVM = useSeasonsVM();

  const [fields, setFields] = useState<TFieldListItem[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string>("");
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [trials, setTrials] = useState<TTrialWithYield[]>([]);
  const [yieldUnit, setYieldUnit] = useState("");

  // Load fields on mount
  useEffect(() => {
    const loadFields = async () => {
      const seasonId = seasonsVM.currentSeasonId;
      if (!seasonId) return;
      const fieldList = await getFieldsWithTrials(seasonId);
      setFields(fieldList);
      if (fieldList.length > 0 && !selectedFieldId) {
        setSelectedFieldId(fieldList[0].id);
      }
    };
    loadFields();
  }, [seasonsVM.currentSeasonId, selectedFieldId]);

  // Set default season
  useEffect(() => {
    if (!selectedSeasonId && seasonsVM.currentSeasonId) {
      setSelectedSeasonId(seasonsVM.currentSeasonId);
    }
  }, [seasonsVM.currentSeasonId, selectedSeasonId]);

  // Fetch trial yield data when field/season changes
  const fetchTrials = useCallback(async () => {
    if (!selectedFieldId || !selectedSeasonId) return;
    setLoading(true);
    try {
      const result = await getTrialsYieldSummary(
        selectedFieldId,
        selectedSeasonId,
        settingsStore.units
      );
      setTrials(result.trials);
      setYieldUnit(result.unit);
    } catch (e) {
      console.error("Failed to fetch trial yields:", e);
      setTrials([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFieldId, selectedSeasonId, settingsStore.units]);

  useEffect(() => {
    fetchTrials();
  }, [fetchTrials]);

  // Bar chart data
  const chartData = useMemo(
    () =>
      trials.map((t, i) => ({
        name: t.name,
        yield: t.yield.avgYield,
        mass: t.yield.totalMass,
        color: TRIAL_COLORS[i % TRIAL_COLORS.length],
      })),
    [trials]
  );

  // Totals
  const totals = useMemo(() => {
    let totalArea = 0;
    let totalMass = 0;
    let totalYieldWeighted = 0;
    for (const t of trials) {
      totalArea += t.areaHectares;
      totalMass += t.yield.totalMass;
      totalYieldWeighted += t.yield.avgYield * t.areaHectares;
    }
    const avgYield = totalArea > 0 ? totalYieldWeighted / totalArea : 0;
    return {
      area: Math.round(totalArea * 100) / 100,
      mass: Math.round(totalMass * 100) / 100,
      avgYield: Math.round(avgYield * 100) / 100,
    };
  }, [trials]);

  const formatSeasonLabel = (season: { from: Date; to: Date }): string => {
    const opts: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" };
    const from = season.from.toLocaleDateString("en-US", opts);
    const to = season.to.toLocaleDateString("en-US", opts);
    return `${from} — ${to}`;
  };

  return (
    <Root>
      {/* Filters */}
      <FiltersRow>
        <FilterGroup>
          <Label>{lang.analytics?.fieldLabel ?? "Field"}</Label>
          <StyledSelect
            value={selectedFieldId}
            onChange={(e) => setSelectedFieldId(e.target.value)}
          >
            {fields.length === 0 && (
              <option value="">{"No fields"}</option>
            )}
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </StyledSelect>
        </FilterGroup>

        <FilterGroup>
          <Label>{lang.analytics?.seasonLabel ?? "Season"}</Label>
          <StyledSelect
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
          >
            {seasonsVM.seasons.map((s) => (
              <option key={s.id} value={s.id}>
                {formatSeasonLabel(s)}
              </option>
            ))}
          </StyledSelect>
        </FilterGroup>
      </FiltersRow>

      {/* Content */}
      {loading ? (
        <LoadingLayout>
          <Spinner />
        </LoadingLayout>
      ) : trials.length === 0 ? (
        <EmptyState>
          {lang.analytics?.noTrials ??
            "No trials found for this field. Add trials on the Fields page by editing a field."}
        </EmptyState>
      ) : (
        <>
          {/* Summary Table */}
          <Card>
            <Title>{lang.analytics?.trialsSummaryTitle ?? "Trial yield summary"}</Title>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>{lang.analytics?.trialNameColumn ?? "Trial / Variety"}</Th>
                  <Th style={{ textAlign: "right" }}>
                    {lang.analytics?.areaColumn ?? "Area"} (ha)
                  </Th>
                  <Th style={{ textAlign: "right" }}>
                    {lang.analytics?.avgYieldColumn ?? "Avg yield"}{" "}
                    {yieldUnit ? `(${yieldUnit})` : ""}
                  </Th>
                  <Th style={{ textAlign: "right" }}>
                    {lang.analytics?.totalMassColumn ?? "Total mass"}{" "}
                    ({settingsStore.weightUnit})
                  </Th>
                  <Th style={{ textAlign: "right" }}>
                    {lang.analytics?.hexCountColumn ?? "Data points"}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {trials.map((t, i) => (
                  <tr key={t.id}>
                    <Td>
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: TRIAL_COLORS[i % TRIAL_COLORS.length],
                        }}
                      />
                    </Td>
                    <Td bold>{t.name}</Td>
                    <Td style={{ textAlign: "right" }}>
                      {t.areaHectares.toFixed(2)}
                    </Td>
                    <Td bold style={{ textAlign: "right" }}>
                      {t.yield.avgYield.toFixed(2)}
                    </Td>
                    <Td style={{ textAlign: "right" }}>
                      {t.yield.totalMass.toFixed(2)}
                    </Td>
                    <Td style={{ textAlign: "right" }}>
                      {t.yield.hexCount} / {t.yield.totalHexes}
                    </Td>
                  </tr>
                ))}
                {trials.length > 1 && (
                  <tr>
                    <Td />
                    <Td bold>{lang.analytics?.totalRow ?? "Total"}</Td>
                    <Td bold style={{ textAlign: "right" }}>
                      {totals.area.toFixed(2)}
                    </Td>
                    <Td bold style={{ textAlign: "right" }}>
                      {totals.avgYield.toFixed(2)}
                    </Td>
                    <Td bold style={{ textAlign: "right" }}>
                      {totals.mass.toFixed(2)}
                    </Td>
                    <Td />
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>

          {/* Yield Comparison Chart */}
          {chartData.length > 1 && (
            <Card>
              <Title>
                {lang.analytics?.trialsChartTitle ?? "Yield comparison"}
                {yieldUnit ? ` (${yieldUnit})` : ""}
              </Title>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "#494c5b" }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#899093" }} width={50} />
                  <Tooltip
                    formatter={(value: number) => [
                      `${value.toFixed(2)} ${yieldUnit}`,
                      "Avg yield",
                    ]}
                    contentStyle={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontSize: 13,
                      borderRadius: 6,
                    }}
                  />
                  <Bar dataKey="yield" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </>
      )}
    </Root>
  );
};

export default observer(FieldTrials);
